import {CATALOG_SHEETS} from "@/helpers/catalogColumns";
import {normalizeName} from "@/helpers/import/matchNames";

const NOT_MAPPABLE = ['optionLists', 'freeOptionGroups', 'paidOptionGroups'];
const MIN_PARTIAL_LENGTH = 4;
const EXACT_SCORE = 1000;
const EXAMPLE_COUNT = 3;

export const MAPPABLE_COLUMNS = CATALOG_SHEETS
    .find(sheet => sheet.name === 'Products')
    .columns
    .filter(column => !column.exportOnly && !NOT_MAPPABLE.includes(column.key));

export const REQUIRED_FIELDS = MAPPABLE_COLUMNS.filter(column => column.required).map(column => column.key);

const SYNONYMS = {
    name: ['nom', 'naam', 'designation', 'libelle', 'label', 'article', 'artikel', 'produit', 'product', 'titre', 'title'],
    group: ['groupe', 'groep', 'cat', 'categorie', 'category', 'categorieen', 'famille', 'family', 'rayon', 'departement', 'department', 'productgroup'],
    priceVatInclIn: ['prix', 'price', 'prijs', 'prixttc', 'prixvente', 'prixventettc', 'pvttc', 'verkoopprijs', 'pricein'],
    priceVatInclOut: ['prixemporter', 'prixaemporter', 'takeawayprice', 'priceout', 'prijsafhaal'],
    vatIn: ['tva', 'vat', 'btw', 'tauxtva', 'vatrate', 'vatin'],
    vatOut: ['tvaemporter', 'tvaaemporter', 'vatout', 'btwafhaal'],
    barcodes: ['ean', 'ean13', 'codeean', 'eancode', 'barcode', 'barcodes', 'codebarre', 'codebarres', 'gtin', 'upc', 'streepjescode'],
    descriptionFr: ['description', 'descriptionfr'],
    descriptionNl: ['omschrijving', 'beschrijving', 'descriptionnl'],
    descriptionEn: ['descriptionen'],
    color: ['couleur', 'color', 'colour', 'kleur'],
    imageUrl: ['image', 'imageurl', 'photo', 'picture', 'img', 'afbeelding', 'foto'],
    externalReference: ['reference', 'ref', 'refarticle', 'refproduit', 'referencearticle', 'sku', 'plu', 'codearticle', 'artikelcode', 'artikelnummer', 'externalreference'],
    purchasePriceVatExcl: ['prixachat', 'prixdachat', 'cout', 'cost', 'purchaseprice', 'aankoopprijs', 'inkoopprijs'],
    orderIndex: ['ordre', 'order', 'position', 'volgorde', 'rang', 'orderindex'],
};

function compact(value) {
    return normalizeName(value).replace(/[^a-z0-9]/g, '');
}

function aliasesOf(key, labels) {
    return [...new Set([key, ...(SYNONYMS[key] || []), ...(labels[key] || [])].map(compact).filter(Boolean))];
}

function matchScore(header, aliases) {
    if (aliases.includes(header)) return EXACT_SCORE;
    return aliases.reduce((best, alias) => {
        const shorter = Math.min(alias.length, header.length);
        if (shorter < MIN_PARTIAL_LENGTH) return best;
        return alias.includes(header) || header.includes(alias) ? Math.max(best, shorter) : best;
    }, 0);
}

export function guessColumnMapping(header, labels = {}) {
    const candidates = [];
    header.forEach((name, index) => {
        const compacted = compact(name);
        if (!compacted) return;
        MAPPABLE_COLUMNS.forEach((column, order) => {
            const score = matchScore(compacted, aliasesOf(column.key, labels));
            if (score) candidates.push({index, field: column.key, score, order});
        });
    });

    const mapping = {};
    const usedFields = new Set();
    candidates
        .sort((a, b) => b.score - a.score || a.order - b.order || a.index - b.index)
        .forEach(({index, field}) => {
            if (mapping[index] || usedFields.has(field)) return;
            mapping[index] = field;
            usedFields.add(field);
        });
    return mapping;
}

export function columnExamples(rows, index) {
    const examples = [];
    for (const row of rows) {
        const value = String(row.cells[index] ?? '').trim();
        if (value && !examples.includes(value)) examples.push(value);
        if (examples.length === EXAMPLE_COUNT) break;
    }
    return examples;
}

export function mappedProductSheet(source, mapping) {
    return {
        name: 'Products',
        header: source.header.map((_, index) => mapping[index] || ''),
        rows: source.rows,
    };
}
